import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';
import { useApis } from '../../../api/useApiClient';
import { useNotification } from "../../../context/NotificationContext";

import {
    Box,
    CircularProgress,
    Button,
    TableContainer,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Typography,
    Modal,
    IconButton,
    Alert,
    Paper,
    Stack,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField
} from '@mui/material';

import PaymentIcon from '@mui/icons-material/Payment';
import ViewPaymentsIcon from '@mui/icons-material/ManageSearch';
import EmailIcon from '@mui/icons-material/Email';
import PrintIcon from '@mui/icons-material/Print';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BuildIcon from '@mui/icons-material/Build';
import BlockIcon from '@mui/icons-material/Block';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';

import InvoiceTemplate from './InvoiceTemplate';
import PaymentAllocationsList from '../../Payments/PaymentAllocationsList';

import MakePaymentModal from './MakePaymentModal';



export default function InvoiceDetails() {

    const { id } = useParams(); // invoice id
    const { getTenant } = useTenant();
    const { tenantApi } = useApis();
    const { showNotification } = useNotification();
    const navigate = useNavigate();

    // Load tenant details
    const tenant = getTenant();

    const [invoice, setInvoice] = useState({});
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openPaymentAllocationsModal, setOpenPaymentAllocationsModal] = useState(false);
    const [openMakePaymentModal, setOpenMakePaymentModal] = useState(false);
    const [openVoidDialog, setOpenVoidDialog] = useState(false);
    const [openUncollectableDialog, setOpenUncollectableDialog] = useState(false);
    const [actionReason, setActionReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const [refresh, setRefresh] = useState(1);

    async function refreshInvoice() {
        // increment refresh to trigger useEffect
        console.debug("Refreshing invoice data");
        setRefresh(prev => prev + 1);
    }

    async function loadInvoiceData(invoiceId) {
        try {
            setLoading(true);
            const invoiceRes = await tenantApi(`/tenants/sales/invoices/${invoiceId}`);
            setInvoice(invoiceRes.data);
        } catch (e) {
            console.error(e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to load invoice details. Please try again later.`, "error");

        } finally {
            setLoading(false);
        }
    };

    async function loadInvoiceItems(invoiceId) {
        try {
            const itemsRes = await tenantApi(`/tenants/sales/invoices/${invoiceId}/items`);
            setInvoiceItems(itemsRes.data || []);
        } catch (e) {
            console.error(e);
            if (e.message)
                showNotification(e.message, "error");
            else
                showNotification(`Failed to load invoice items. Please try again later.`, "error");
        }
    };

    // Handle payment modal
    async function handleAddPayment() {
        // TO be implemented
    }

    // Handle void invoice
    async function handleVoidInvoice() {
        if (!actionReason.trim()) {
            showNotification('Please provide a reason for voiding the invoice', 'error');
            return;
        }

        setActionLoading(true);
        try {
            await tenantApi(`/tenants/sales/invoices/${id}/void`, {
                method: 'POST',
                body: { reason: actionReason }
            });
            showNotification('Invoice voided successfully', 'success');
            setOpenVoidDialog(false);
            setActionReason('');
            refreshInvoice();
        } catch (e) {
            console.error(e);
            showNotification(e.message || 'Failed to void invoice', 'error');
        } finally {
            setActionLoading(false);
        }
    }

    // Handle mark uncollectable
    async function handleMarkUncollectable() {
        if (!actionReason.trim()) {
            showNotification('Please provide a reason for marking as uncollectable', 'error');
            return;
        }

        setActionLoading(true);
        try {
            await tenantApi(`/tenants/sales/invoices/${id}/mark-uncollectable`, {
                method: 'POST',
                body: { reason: actionReason }
            });
            showNotification('Invoice marked as uncollectable', 'success');
            setOpenUncollectableDialog(false);
            setActionReason('');
            refreshInvoice();
        } catch (e) {
            console.error(e);
            showNotification(e.message || 'Failed to mark invoice as uncollectable', 'error');
        } finally {
            setActionLoading(false);
        }
    }

    // Load invoice data on component mount or when id/refresh changes
    // Refresh can be changed when a payment is made, to reload the invoice data
    useEffect(() => {
        if (id) {
            loadInvoiceData(id);
        }
    }, [id, refresh]);

    // Load invoice items on component mount or when id changes
    useEffect(() => {
        if (id) {
            loadInvoiceItems(id);
        }
    }, [id]);


    // Debug
    useEffect(() => {
        console.log("Loaded invoice:", invoice);
    }, [invoice]);

    const handlePrint = () => {
        navigate(`/print/invoices`, { state: { title: tenant?.name, invoice, invoiceItems } });
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>

            {/* Payment Allocations List */}
            <Modal open={openPaymentAllocationsModal} onClose={() => setOpenPaymentAllocationsModal(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '80%' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: { xs: 2, sm: 3, md: 4 },
                }}>
                    {/* A close button on the top right */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton onClick={() => setOpenPaymentAllocationsModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <PaymentAllocationsList filters={{ payableType: 'INVOICE', payableId: invoice.id }} />
                </Box>
            </Modal>


            {/* Make Payment Form */}
            <MakePaymentModal
                open={openMakePaymentModal}
                onSubmit={refreshInvoice}
                onClose={() => setOpenMakePaymentModal(false)}
                invoice={invoice}
            />

            {/* Invoice template section */}
            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                {/* A4 Paper Container to display Invoice Template*/}
                <Box margin={{ xs: 0, sm: 2 }} mt={{ xs: 2, sm: 4 }} padding={{ xs: 1, sm: 2 }}>
                    <InvoiceTemplate title={tenant?.name} invoice={invoice} invoiceItems={invoiceItems} />
                </Box>
            </Box>

            {/* Display canceled alert if the invoice is canceled */}
            {invoice.status === 'VOID' && (
                <Box margin={{ xs: 1, sm: 2 }} mt={{ xs: 2, sm: 4 }} padding={{ xs: 1, sm: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }} icon={<BlockIcon />}>
                        This invoice has been voided.
                        {invoice.voidReason && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Reason:</strong> {invoice.voidReason}
                            </Typography>
                        )}
                    </Alert>
                </Box>

            )}

            {/* Display uncollectable alert */}
            {invoice.status === 'UNCOLLECTABLE' && (
                <Box margin={{ xs: 1, sm: 2 }} mt={{ xs: 2, sm: 4 }} padding={{ xs: 1, sm: 2 }}>
                    <Alert severity="error" sx={{ mb: 2 }} icon={<MoneyOffIcon />}>
                        This invoice has been marked as uncollectable (bad debt).
                        {invoice.uncollectableReason && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>Reason:</strong> {invoice.uncollectableReason}
                            </Typography>
                        )}
                    </Alert>
                </Box>
            )}


            {/* Display other items only if the invoice is in ISSUED state */}
            {invoice.status === 'ISSUED' && (

                <>
                    {/* Action buttons to Print / Send invoice */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2,
                        px: { xs: 2, sm: 0 }
                    }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<EmailIcon />}
                            fullWidth
                            sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
                            onClick={() => showNotification("Email functionality not implemented yet.", "info")}
                        >
                            Send via Email
                        </Button>

                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PrintIcon />}
                            fullWidth
                            sx={{ maxWidth: { xs: '100%', sm: 'auto' } }}
                            onClick={handlePrint}
                        >
                            Print Invoice
                        </Button>

                    </Box>
                    {/* Payment summary table */}
                    <Box margin={{ xs: 1, sm: 2 }} mt={{ xs: 2, sm: 4 }} padding={{ xs: 1, sm: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            Payment Summary:
                        </Typography>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 650 }}>
                                <TableBody>
                                    <TableRow>
                                        <TableCell>Total Amount</TableCell>
                                        <TableCell>Total Paid</TableCell>
                                        <TableCell><strong>Total On Hold</strong></TableCell>
                                        <TableCell><strong>Total Outstanding</strong></TableCell>
                                        <TableCell></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>

                                    <TableRow>
                                        <TableCell>{invoice.netTotal} {invoice.currency}</TableCell>
                                        <TableCell>{invoice.totalPaidAmount} {invoice.currency}</TableCell>
                                        <TableCell>
                                            {invoice.totalOnHoldAmount} {invoice.currency}
                                        </TableCell>
                                        <TableCell>
                                            <strong>{invoice.totalOutstandingAmount} {invoice.currency}</strong>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<ViewPaymentsIcon />}
                                                onClick={() => setOpenPaymentAllocationsModal(true)}
                                                disabled={invoice.paymentStatus === 'UNPAID'}
                                            >
                                                View Payments
                                            </Button>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                startIcon={<PaymentIcon />}
                                                onClick={() => setOpenMakePaymentModal(true)}
                                                disabled={invoice.paymentStatus === 'FULLY_PAID'}
                                            >
                                                Make Payment
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Invoice Actions - Void / Mark Uncollectable */}
                    <Box margin={{ xs: 1, sm: 2 }} mt={{ xs: 2, sm: 4 }} padding={{ xs: 1, sm: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            Invoice Actions:
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            {invoice.paymentStatus === 'UNPAID' && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<BlockIcon />}
                                    onClick={() => setOpenVoidDialog(true)}
                                >
                                    Void Invoice
                                </Button>
                            )}
                            {invoice.paymentStatus !== 'FULLY_PAID' && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<MoneyOffIcon />}
                                    onClick={() => setOpenUncollectableDialog(true)}
                                >
                                    Mark as Uncollectable
                                </Button>
                            )}
                        </Stack>
                    </Box>
                </>
            )}

            {/* Void Invoice Dialog */}
            <Dialog open={openVoidDialog} onClose={() => setOpenVoidDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Void Invoice</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Are you sure you want to void this invoice? This action cannot be undone.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for voiding"
                        fullWidth
                        multiline
                        rows={3}
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenVoidDialog(false); setActionReason(''); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleVoidInvoice}
                        color="warning"
                        variant="contained"
                        disabled={actionLoading || !actionReason.trim()}
                    >
                        {actionLoading ? 'Voiding...' : 'Void Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mark Uncollectable Dialog */}
            <Dialog open={openUncollectableDialog} onClose={() => setOpenUncollectableDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Mark as Uncollectable</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Are you sure you want to mark this invoice as uncollectable (bad debt)? This action cannot be undone.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for marking as uncollectable"
                        fullWidth
                        multiline
                        rows={3}
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenUncollectableDialog(false); setActionReason(''); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleMarkUncollectable}
                        color="error"
                        variant="contained"
                        disabled={actionLoading || !actionReason.trim()}
                    >
                        {actionLoading ? 'Processing...' : 'Mark Uncollectable'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}